<template>
    <header class="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 h-auto shadow-sm">
        <BarraAcessibilidade v-if="barraAcessibilidadeAtiva" />
        <div class="container mx-auto px-2 sm:px-3 py-2 h-full flex items-center justify-between gap-2">
            <div class="hidden lg:block">
                <a href="/" class="flex items-center hover:opacity-90 transition">
                    <img v-if="logoBranca" :src="logoBrancaHorizontal" alt="Prefeitura Municipal de Parnaíba"
                        class="h-12" />
                    <img v-else-if="logo.logo_herder_top" :src="logo.logo_herder_top"
                        alt="Prefeitura Municipal de Parnaíba" class="h-12" />
                    <div v-else class="flex items-center gap-2 text-blue-900 text-sm font-medium">
                        <span class="text-xs bg-gray-200 px-2 py-1 rounded">Sem logo</span>
                    </div>
                </a>
            </div>
            <div class="lg:hidden flex items-center flex-shrink-0">
                <a href="/" class="flex items-center hover:opacity-90 transition">
                    <img v-if="logoBranca" :src="logoBrancaVertical" alt="Prefeitura"
                        class="h-16 w-auto object-contain" />
                    <img v-else-if="logo.logo_herder_top" src="@/Assets/Logo PMP AZUL V.png" alt="Prefeitura"
                        class="h-16 w-auto object-contain" />
                    <div v-else class="flex items-center gap-2 text-blue-900">
                        <span class="text-[10px] bg-gray-200 px-2 py-1 rounded font-medium">PARNAÍBA</span>
                    </div>
                </a>
            </div>
            <nav class="hidden md:flex items-center gap-5 text-admin-primary text-sm font-medium">
                <template v-for="item in menuItems" :key="item.route">
                    <a v-if="isExternal(item.route)" :href="item.route.trim()" target="_blank" rel="noopener noreferrer"
                        class="flex items-center gap-1 px-3 py-1 rounded-full transition-colors duration-200" :class="{
                            'bg-admin-primary text-white font-bold': item.label === 'Transparência',
                            'hover:bg-admin-primary hover:text-white': item.label !== 'Transparência'
                        }" :title="item.label + ' (abre em nova aba)'">
                        <component :is="getIcon(item.label)" class="w-4 h-4" />
                        {{ item.label }}
                    </a>
                    <Link v-else :href="item.route"
                        class="flex items-center gap-1 px-3 py-1 rounded-full transition-colors duration-200" :class="{
                            'bg-admin-primary text-white font-bold': item.label === 'Transparência',
                            'hover:bg-admin-primary hover:text-white': item.label !== 'Transparência'
                        }" :title="item.label" :aria-current="route().current(item.route) ? 'page' : undefined">
                        <component :is="getIcon(item.label)" class="w-4 h-4" />
                        {{ item.label }}
                    </Link>
                </template>
            </nav>
            <div class="md:hidden flex items-center gap-1 sm:gap-1.5 overflow-x-auto no-scrollbar">
                <Link href="/pagina/ouvidoria"
                    class="flex items-center gap-1 text-[10px] sm:text-xs font-medium text-gray-700 hover:text-admin-primary transition-colors whitespace-nowrap flex-shrink-0">
                    <component :is="getIcon('Ouvidoria')" class="w-3.5 h-3.5 flex-shrink-0" />
                    <span>Ouvidoria</span>
                </Link>
                <a href="https://transparencia.parnaiba.pi.gov.br/transparencia" target="_blank"
                    rel="noopener noreferrer" title="Transparência (abre em nova aba)"
                    class="flex items-center gap-1 text-[10px] sm:text-xs font-medium text-admin-primary hover:text-blue-700 transition-colors whitespace-nowrap flex-shrink-0">
                    <EyeIcon class="w-3.5 h-3.5 flex-shrink-0" />
                    <span>Transparência</span>
                </a>
            </div>
        </div>
    </header>
</template>

<script setup>
import { computed, h } from 'vue'
import { usePage, Link } from '@inertiajs/vue3'
import { Eye as EyeIcon } from 'lucide-vue-next'
import BarraAcessibilidade from '@/Components/Welcom/BarraAcessibilidade.vue'
import { useAltoContraste } from '@/Composables/useAltoContraste'
import { barraAcessibilidadeVisivel, opcaoLigada } from '@/Utils/configuracaoSite'
import logoBrancaHorizontal from '@/Assets/Logo PMP BRANCO H.png'
import logoBrancaVertical from '@/Assets/Logo PMP BRANCO V.png'

// No alto contraste o fundo fica preto: usa a logo branca.
const { ativo: contrasteAtivo } = useAltoContraste()
const { props } = usePage()
// Ligada/desligada em Configurações > Opções do site.
const barraAcessibilidadeAtiva = computed(() => barraAcessibilidadeVisivel(usePage().props.configuracaoSite))
// A logo branca só faz sentido com o contraste disponível na barra.
const logoBranca = computed(() => contrasteAtivo.value && barraAcessibilidadeAtiva.value
    && opcaoLigada(usePage().props.configuracaoSite, 'barra_contraste'))
const logo = props.logo || {}

const icons = {
    'Diário Oficial': () => h('svg', {
        xmlns: 'http://www.w3.org/2000/svg',
        class: 'w-4 h-4',
        fill: 'none',
        viewBox: '0 0 24 24',
        stroke: 'currentColor',
        innerHTML: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H2M2 6v6M2 12v6" />',
    }),
    'Ouvidoria': () => h('svg', {
        xmlns: 'http://www.w3.org/2000/svg',
        class: 'w-4 h-4',
        fill: 'none',
        viewBox: '0 0 24 24',
        stroke: 'currentColor',
        innerHTML: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v6a2 2 0 002 2h2v4a2 2 0 002 2h2a2 2 0 002-2v-4h2a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 002-2h2a2 2 0 002 2m-6 9l2 2 4-4" />',
    }),
    'Transparência': () => h('svg', {
        xmlns: 'http://www.w3.org/2000/svg',
        class: 'w-4 h-4',
        fill: 'none',
        viewBox: '0 0 24 24',
        stroke: 'currentColor',
        innerHTML: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />',
    }),
    'Legislação': () => h('svg', {
        xmlns: 'http://www.w3.org/2000/svg',
        class: 'w-4 h-4',
        fill: 'none',
        viewBox: '0 0 24 24',
        stroke: 'currentColor',
        innerHTML: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7M5 2h14v16H5V2z" />',
    }),
    'Política de Privacidade': () => h('svg', {
        xmlns: 'http://www.w3.org/2000/svg',
        class: 'w-4 h-4',
        fill: 'none',
        viewBox: '0 0 24 24',
        stroke: 'currentColor',
        innerHTML: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v6h8z" />',
    }),
}

const getIcon = (label) => icons[label] || (() => h('span'))

const menuItems = [
    { label: 'Diário Oficial', route: 'https://dome.parnaiba.pi.gov.br/  ' },
    { label: 'Ouvidoria', route: '/pagina/ouvidoria' },
    { label: 'Transparência', route: 'https://transparencia.parnaiba.pi.gov.br/transparencia' },
    { label: 'Legislação', route: '/legislacao' },
    { label: 'Política de Privacidade', route: '/politica-privacidade' },
]

const isExternal = (url) => /^https?:\/\//.test(url.trim())
</script>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
    display: none;
}

.no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
}
</style>
